import { useEffect, useState } from "react";
import { Button, Message, Modal, Select, Spin } from "@/frontend/components/common";
import { subCategoryService, tournamentService } from "@/frontend/services";
import type { SubCategoryResponseDTO } from "@/shared/dto";

interface TournamentAutomationModalProps {
  open: boolean;
  tournamentId: string;
  autoCategories?: string[];
  onCancel: () => void;
  onSuccess: () => void;
}

async function fetchAllSubCategories(): Promise<SubCategoryResponseDTO[]> {
  const firstPage = await subCategoryService.getPaginate({ page: 1, pageSize: 100 });
  if (!firstPage.success || !firstPage.data) {
    return [];
  }

  const categories = [...firstPage.data.data];
  for (let page = 2; page <= firstPage.data.pagination.totalPages; page += 1) {
    const nextPage = await subCategoryService.getPaginate({ page, pageSize: 100 });
    if (nextPage.success && nextPage.data) {
      categories.push(...nextPage.data.data);
    }
  }

  return categories;
}

export function TournamentAutomationModal({
  open,
  tournamentId,
  autoCategories = [],
  onCancel,
  onSuccess,
}: TournamentAutomationModalProps) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState<SubCategoryResponseDTO[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setSelectedCategoryIds(autoCategories);
    setLoading(true);
    fetchAllSubCategories()
      .then((loaded) => setCategories(loaded))
      .catch(() => Message.error("No se pudieron cargar las categorías"))
      .finally(() => setLoading(false));
  }, [open]);

  useEffect(() => {
    if (!open) {
      setSelectedCategoryIds([]);
      setCategories([]);
    }
  }, [open]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await tournamentService.update(tournamentId, {
        autoCategories: selectedCategoryIds,
      });

      if (!response.success) {
        Message.error(response.error ?? "No se pudieron guardar las categorías");
        return;
      }

      Message.success("Categorías guardadas correctamente");
      onSuccess();
    } catch {
      Message.error("No se pudieron guardar las categorías");
    } finally {
      setSaving(false);
    }
  };

  const categoryOptions = categories.map((c) => ({
    label: c.category,
    value: c.id,
  }));

  return (
    <Modal
      title="Automatizar Torneos"
      open={open}
      onCancel={onCancel}
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onCancel}>Cancelar</Button>
          <Button type="primary" loading={saving} onClick={handleSave}>
            Guardar
          </Button>
        </div>
      }
      width={600}
      destroyOnHidden
    >
      {loading ? (
        <div className="py-12 flex justify-center">
          <Spin size="large" tip="Cargando categorías..." />
        </div>
      ) : (
        <div className="space-y-4 mt-4">
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Categorias</p>
            <Select
              mode="multiple"
              allowClear
              showSearch
              size="large"
              placeholder="Selecciona una o varias categorias"
              notFoundContent="No hay categorias disponibles."
              value={selectedCategoryIds}
              onChange={(value) => setSelectedCategoryIds((value as string[]) ?? [])}
              options={categoryOptions}
              className="w-full"
              maxTagCount="responsive"
              optionFilterProp="label"
              filterOption={(input, option) =>
                String(option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
            <p className="mt-2 text-xs text-gray-500">
              {selectedCategoryIds.length > 0
                ? `${selectedCategoryIds.length} categoria(s) seleccionada(s)`
                : "Puedes seleccionar varias categorias a la vez."}
            </p>
          </div>
        </div>
      )}
    </Modal>
  );
}
